import { useCallback, useEffect, useRef, useState } from 'react';

import { useStateRef } from './useStateRef';

type InnerFunction = (...args: any[]) => any;
/**
 * 该 hook 包裹的函数被调用时根据 ready 状态有不同的表现
 *  1. 未 ready 则该函数会被加入到队列中
 *  2. ready 时将会将队列中的函数重新执行
 * 注意：传入的 func 使用 useCallback 包裹且 ready 期间不能重新构造, 否则可能出现预期外的情况
 */
export function useReadyFunction<T extends InnerFunction>(
  func: T,
  { last }: { last?: number } = {}
) {
  const [ready, setReady] = useState(false);
  const readyRef = useStateRef(ready);
  const queueRef = useRef<(() => any)[]>([]);
  const wrappedFunction = useCallback(
    (...args: any[]) => {
      if (readyRef.current === false) queueRef.current.push(() => func(...args));
      else func(...args);
    },
    [func, readyRef]
  );

  useEffect(() => {
    if (!ready) return;

    if (typeof last === 'number' && queueRef.current.length > last) {
      queueRef.current = queueRef.current.slice(-last);
    }
    queueRef.current.forEach((fn) => fn());
    queueRef.current = [];
  }, [last, ready]);

  return {
    ready,
    setReady,
    wrappedFunction,
  };
}
